/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import Text from '@/shared/ui/components/basic/Text';
import UnstyledButton from '@/shared/ui/components/basic/UnstyledButton';
import { cn } from '@/shared/ui/utils/tailwind';

const trackCn = cn(
  'relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center',
  'rounded-full transition-colors duration-200',
);

const knobCn = cn('inline-block h-3.5 w-3.5 rounded-full bg-white transition-transform');

export interface BlockSwitchProps {
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
  /** Names the switch for assistive tech when the visible label sits apart. */
  ariaLabel?: string;
  testId?: string;
}

/**
 * The on/off switch used by the `toggles` mid.
 *
 * A real `role="switch"` rather than a styled checkbox, so the state is
 * announced as on/off instead of checked/unchecked — the row already reads
 * as a setting, not as an item being selected.
 *
 * @param props - Component props.
 * @returns The switch element.
 */
export function BlockSwitch({ checked, onChange, disabled, ariaLabel, testId }: BlockSwitchProps) {
  return (
    <UnstyledButton
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      disabled={disabled}
      data-testid={testId}
      onClick={() => onChange(!checked)}
      className={cn(
        trackCn,
        checked ? 'bg-(--seed-base)' : 'bg-(--border-subtle)',
        disabled && 'cursor-not-allowed opacity-50',
      )}
    >
      <Text component="span" className={cn(knobCn, checked ? 'translate-x-4.5' : 'translate-x-0.5')} />
    </UnstyledButton>
  );
}
